/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Icon } from '@/components/Icon';
import { Button, revealElement } from '@/design-system';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/stores/uiStore';
import {
  markTourSeen,
  resolveSteps,
  tourById,
  tourForPath,
  type PageTour,
  type ResolvedStep,
} from './tourSteps';
import {
  boxOf,
  dockSheetTop,
  liftAboveSheet,
  PAD,
  placeCard,
  SHEET_BELOW,
  type Box,
} from './tourLayout';
import './tour.css';

/** The element focus goes back to when the tour ends: its launcher, else the Help button. */
function returnFocus(opener: Element | null) {
  const target =
    opener instanceof HTMLElement && opener.isConnected && opener !== document.body
      ? opener
      : document.querySelector<HTMLElement>('[data-tour="help"]');
  target?.focus();
}

/**
 * Contextual product tour of the page on screen: at most six stops, each a spotlight on its target
 * with a card beside it (clamped in the viewport, never covering the target) or, below 600px, a
 * bottom sheet with the target scrolled into view above it. Stops whose target is hidden are
 * skipped. Left / Right step, Escape ends, focus stays in the card and goes back to the launcher.
 *
 * Mounted only while the tour is open, so each launch starts fresh at the first stop.
 */
export function TourOverlay() {
  const { t } = useTranslation();
  const endTour = useUiStore((s) => s.endTour);
  const tourKind = useUiStore((s) => s.tourKind);
  const { pathname } = useLocation();
  const titleId = useId();
  const bodyId = useId();

  // Fixed for the life of the overlay: the tour, the stops visible when it started, the page it
  // started on and the control that launched it.
  const [{ tour, steps, startPath, opener }] = useState(() => {
    const tour: PageTour | null =
      tourKind === 'explorer' ? tourById('explorer') : tourForPath(pathname);
    return {
      tour,
      steps: tour ? resolveSteps(tour) : ([] as ResolvedStep[]),
      startPath: pathname,
      opener: document.activeElement,
    };
  });

  const [index, setIndex] = useState(0);
  const [target, setTarget] = useState<Box | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [sheetAtTop, setSheetAtTop] = useState(false);
  const cardRef = useRef<HTMLElement>(null);

  const step = steps[index];
  const isFirst = index === 0;
  const isLast = index === steps.length - 1;

  const finish = useCallback(() => endTour(), [endTour]);
  const next = useCallback(() => {
    if (isLast) finish();
    else setIndex((i) => i + 1);
  }, [isLast, finish]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  // Starting the tour counts as having been offered it; nothing to show ends it at once.
  useEffect(() => {
    if (tour) markTourSeen(tour.id);
    if (!steps.length) finish();
  }, [tour, steps, finish]);

  // Focus moves into the card, and back to the launcher when the tour ends.
  useEffect(() => {
    cardRef.current?.focus();
    return () => returnFocus(opener);
  }, [opener]);

  // Leaving the page ends its tour.
  useEffect(() => {
    if (pathname !== startPath) finish();
  }, [pathname, startPath, finish]);

  // Lay the stop out: the target's box and, from 600px up, the card beside it. The stylesheet
  // docks the card as a bottom sheet on phones.
  const layout = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.selector);
    const box = el ? boxOf(el) : null;
    const card = cardRef.current;
    setTarget(box);
    setPos(
      box && card && window.innerWidth >= SHEET_BELOW
        ? placeCard(box, { width: card.offsetWidth, height: card.offsetHeight })
        : null,
    );
  }, [step]);

  // Each stop first brings its target into view (instantly, inside whatever scrolls it; above the
  // sheet on a phone), then lays out. Measuring the DOM after it commits is what this effect is for.
  useLayoutEffect(() => {
    if (!step) return;
    const el = document.querySelector(step.selector);
    let unlift = () => {};
    let top = false;
    if (el) {
      revealElement(el);
      const card = cardRef.current;
      if (card && window.innerWidth < SHEET_BELOW) {
        // Measured as a bottom sheet whichever edge the previous stop docked it to.
        unlift = liftAboveSheet(el, window.innerHeight - card.offsetHeight);
        top = dockSheetTop(el, card.offsetHeight);
      }
    }
    setSheetAtTop(top);
    layout();
    return unlift;
  }, [step, layout]);

  // Keep the spotlight on its target while the window resizes or anything scrolls.
  useEffect(() => {
    window.addEventListener('resize', layout);
    window.addEventListener('scroll', layout, true);
    return () => {
      window.removeEventListener('resize', layout);
      window.removeEventListener('scroll', layout, true);
    };
  }, [layout]);

  // Left / Right step, Escape ends; Tab cycles inside the card.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
      } else if (e.key === 'Tab') {
        const card = cardRef.current;
        if (!card) return;
        const items = Array.from(card.querySelectorAll<HTMLElement>('button:not([disabled])'));
        const first = items[0];
        const last = items[items.length - 1];
        const inside = card.contains(document.activeElement);
        if (e.shiftKey && (document.activeElement === first || !inside)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, back, finish]);

  if (!tour || !step) return null;

  // The ring is clamped to the viewport so a full-bleed target never pushes it off screen.
  const spot = target
    ? (() => {
        const left = Math.max(0, target.left - PAD);
        const top = Math.max(0, target.top - PAD);
        const right = Math.min(window.innerWidth, target.left + target.width + PAD);
        const bottom = Math.min(window.innerHeight, target.top + target.height + PAD);
        return {
          left,
          top,
          width: Math.max(0, right - left),
          height: Math.max(0, bottom - top),
        };
      })()
    : null;

  return (
    <div className="tour-root" data-tour-id={tour.id}>
      <div className={cn('tour-backdrop', !spot && 'is-dim')} aria-hidden="true" />
      {spot && <div className="tour-spot" style={spot} aria-hidden="true" />}

      <section
        ref={cardRef}
        className={cn('tour-card', pos && 'is-placed', sheetAtTop && 'is-top')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        style={pos ? { left: pos.left, top: pos.top } : undefined}
      >
        <div className="tour-card-head">
          <span className="tour-card-ic" aria-hidden="true">
            <Icon name={step.icon} size={18} />
          </span>
          <div className="tour-card-heading" aria-live="polite">
            <span className="tour-eyebrow">
              {t('tour.stepOf', { current: index + 1, total: steps.length })}
            </span>
            <h2 id={titleId} className="tour-card-title">
              {t(step.title)}
            </h2>
          </div>
          <button
            type="button"
            className="iclose tour-x"
            onClick={finish}
            aria-label={t('tour.closeTour')}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <p id={bodyId} className="tour-card-body">
          {t(step.body)}
        </p>

        <div className="tour-card-foot">
          <div className="tour-dots" aria-hidden="true">
            {steps.map((s, i) => (
              <span key={s.id} className={cn('tour-dot', i === index && 'on')} />
            ))}
          </div>
          <div className="tour-actions">
            {!isLast && (
              <Button type="button" size="sm" variant="ghost" onClick={finish}>
                {t('tour.skip')}
              </Button>
            )}
            {!isFirst && (
              <Button type="button" size="sm" variant="outline" onClick={back}>
                {t('tour.back')}
              </Button>
            )}
            <Button type="button" size="sm" onClick={next}>
              {isLast ? t('tour.done') : t('tour.next')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
